import { type ReactNode } from 'react';
import { useDroppable } from '@dnd-kit/core';
import type { CreatorStatus } from '@/types';
import { cn } from '@/utils/cn';
import { ColumnBody, useColumnCount } from './kanban-column-body';

type Props = {
  status: CreatorStatus;
  label: string;
  color: string;
  skeletonCount: number;
  onSelect: (id: string) => void;
};

const COL =
  'flex w-[75vw] min-w-[200px] flex-col snap-center sm:w-[240px] sm:min-w-[240px] sm:snap-align-none';
const DROP_ZONE =
  'flex-1 overflow-y-auto rounded-lg px-xs py-xs transition-colors scrollbar-thin';

function Header({
  label,
  color,
  count,
}: {
  label: string;
  color: string;
  count: number;
}): ReactNode {
  return (
    <div className="flex items-center gap-xs px-xs pb-sm">
      <span className={cn('text-xs font-medium', color)}>{label}</span>
      <span className="rounded-sm bg-white/[0.06] px-1.5 py-0.5 text-[10px] tabular-nums text-text-tertiary">
        {count}
      </span>
    </div>
  );
}

export function KanbanColumn({
  status,
  label,
  color,
  skeletonCount,
  onSelect,
}: Props): ReactNode {
  const { setNodeRef, isOver } = useDroppable({ id: status });
  const count = useColumnCount(status);
  return (
    <div className={COL}>
      <Header label={label} color={color} count={count} />
      <div
        ref={setNodeRef}
        className={cn(DROP_ZONE, isOver && 'bg-white/[0.03]')}
      >
        <ColumnBody
          status={status}
          skeletonCount={skeletonCount}
          onSelect={onSelect}
        />
      </div>
    </div>
  );
}
